import {
  ExpensableGroupsDataTableDocument,
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
  PayrollPolicyPeriodTypesPeriodTypeEnum,
} from 'generated/resources/datatable.graphql'
import {
  BulkAddExpensablesToGroupDocument,
  BulkAddExpensablesToPaymentDocument,
  BulkAddExpensablesToPayrollDocument,
  BulkApproveExpensablesDocument,
  BulkRejectExpensablesDocument,
  BulkRemoveExpensablesFromGroupDocument,
  BulkRemoveExpensablesFromPayrollDocument,
  BulkRestartExpensablesReviewDocument,
  BulkRevertPaidExpensablesDocument,
  BulkSetExpensablesToPaidDocument,
  ExpensablesDataCollectionDocument,
  ExpensablesPendingApprovalWidgetDocument,
  ExpensesApprovedWidgetDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
  ReimbursementsApprovedWidgetDocument,
  ReimbursementsPendingApprovalWidgetDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const showFlashMessage = <Data>(
  handled: Result<Data>,
  success: string = i18n.t('expenses_v2.success_messages.all_items')
) => {
  if (!handled.success) return
  app.setFlash({
    type: 'correct',
    message: success,
  })
}

const refetchQueries = () => [
  ExpensablesDataTableDocument,
  ExpensablesDataCollectionDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataTableDocument,
  MyGroupedExpensablesDataCollectionDocument,

  ExpensablesPendingApprovalWidgetDocument,
  ReimbursementsPendingApprovalWidgetDocument,
  ExpensesApprovedWidgetDocument,
  ReimbursementsApprovedWidgetDocument,
]

const useBulkApprove = () => {
  const [approveExpensables, { loading }] = useMutation(BulkApproveExpensablesDocument, {
    extractorFn: (data) => data.expensesMutations.bulkApproveExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    approve: (ids: number[], overrideApprovalFlow?: boolean) =>
      approveExpensables({ variables: { ids, overrideApprovalFlow } }),
  }
}

const useBulkReject = () => {
  const [rejectExpensables, { loading }] = useMutation(BulkRejectExpensablesDocument, {
    extractorFn: (data) => data.expensesMutations.bulkRejectExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    reject: (ids: number[], reason?: string, overrideApprovalFlow?: boolean) =>
      rejectExpensables({ variables: { ids, reason, overrideApprovalFlow } }),
  }
}

const useBulkRestartReview = () => {
  const [restartReviewExpensables, { loading }] = useMutation(
    BulkRestartExpensablesReviewDocument,
    {
      extractorFn: (data) => data.expensesMutations.bulkRestartReviewExpensesExpensable,
      refetchQueries,
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    loading,
    restartReview: (ids: number[]) => restartReviewExpensables({ variables: { ids } }),
  }
}

const useBulkAddToPayroll = () => {
  const [addToPayrollExpensables, { loading }] = useMutation(BulkAddExpensablesToPayrollDocument, {
    extractorFn: (data) => data.expensesMutations.bulkAddToPayrollExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    addToPayroll: (ids: number[], period: PayrollPolicyPeriodTypesPeriodTypeEnum) =>
      addToPayrollExpensables({ variables: { ids, period } }),
  }
}

const useBulkAddToPayment = () => {
  const [addToPaymentExpensables, { loading }] = useMutation(BulkAddExpensablesToPaymentDocument, {
    extractorFn: (data) => data.expensesMutations.bulkSendToPayWithSepaExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    addToPayment: (ids: number[], dueDate: Date) =>
      addToPaymentExpensables({
        variables: { ids, dueDate: dueDate.toISOString().split('T')[0] + '' },
      }),
  }
}

const useBulkRemoveFromPayroll = () => {
  const [removeFromPayrollExpensables, { loading }] = useMutation(
    BulkRemoveExpensablesFromPayrollDocument,
    {
      extractorFn: (data) => data.expensesMutations.bulkRemoveFromPayrollExpensesExpensable,
      refetchQueries,
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    loading,
    removeFromPayroll: (ids: number[]) => removeFromPayrollExpensables({ variables: { ids } }),
  }
}

const useBulkSetToPaid = () => {
  const [setExpensablesToPaid, { loading }] = useMutation(BulkSetExpensablesToPaidDocument, {
    extractorFn: (data) => data.expensesMutations.bulkSetToPaidExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    setToPaid: (ids: number[]) => setExpensablesToPaid({ variables: { ids } }),
  }
}

const useBulkRevertPaid = () => {
  const [revertPaidExpensables, { loading }] = useMutation(BulkRevertPaidExpensablesDocument, {
    extractorFn: (data) => data.expensesMutations.bulkRevertPaidExpensesExpensable,
    refetchQueries,
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    revertPaid: (ids: number[]) => revertPaidExpensables({ variables: { ids } }),
  }
}

const useBulkAddToGroup = () => {
  const [addExpensablesToGroup, { loading }] = useMutation(BulkAddExpensablesToGroupDocument, {
    extractorFn: (data) => data.expensesMutations.bulkAddToGroupExpensesExpensable,
    refetchQueries: () => [...refetchQueries(), ExpensableGroupsDataTableDocument],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    addToGroup: (ids: number[], groupId: number) =>
      addExpensablesToGroup({ variables: { ids, groupId } }),
  }
}

const useBulkRemoveFromGroup = () => {
  const [removeExpensablesFromGroup, { loading }] = useMutation(
    BulkRemoveExpensablesFromGroupDocument,
    {
      extractorFn: (data) => data.expensesMutations.bulkRemoveFromGroupExpensesExpensable,
      refetchQueries,
      awaitRefetchQueries: true,
      onQueryUpdated: (query) => query.reobserve(),
    }
  )

  return {
    loading,
    removeFromGroup: (ids: number[]) => removeExpensablesFromGroup({ variables: { ids } }),
  }
}

export const useExpensableActions = () => {
  const { approve, loading: approving } = useBulkApprove()
  const { reject, loading: rejecting } = useBulkReject()
  const { restartReview, loading: restartingReview } = useBulkRestartReview()
  const { addToPayroll, loading: addingToPayroll } = useBulkAddToPayroll()
  const { addToPayment, loading: addingToPayment } = useBulkAddToPayment()
  const { removeFromPayroll, loading: removingFromPayroll } = useBulkRemoveFromPayroll()
  const { setToPaid, loading: settingToPaid } = useBulkSetToPaid()
  const { revertPaid, loading: revertingPaid } = useBulkRevertPaid()
  const { addToGroup, loading: addingToGroup } = useBulkAddToGroup()
  const { removeFromGroup, loading: removingFromGroup } = useBulkRemoveFromGroup()

  const loading =
    approving ||
    rejecting ||
    restartingReview ||
    addingToPayroll ||
    addingToPayment ||
    removingFromPayroll ||
    settingToPaid ||
    revertingPaid ||
    addingToGroup ||
    removingFromGroup

  const onRestartReview = async (ids: number[]) => {
    const handled = handleResult(await restartReview(ids), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onApprove = async (ids: number[], overrideApprovalFlow?: boolean) => {
    const handled = handleResult(await approve(ids, overrideApprovalFlow), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onReject = async (ids: number[], reason?: string, overrideApprovalFlow?: boolean) => {
    const handled = handleResult(await reject(ids, reason, overrideApprovalFlow), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onAddToPayroll = async (
    period: PayrollPolicyPeriodTypesPeriodTypeEnum,
    selectedIds: number[]
  ) => {
    const handled = handleResult(await addToPayroll(selectedIds, period), () => null)
    showFlashMessage(handled)
    track(trackingEvents.compensations.createCompensation, {
      source: 'expenses_expense',
      period,
      selectedIds,
    })
    return handled
  }

  const onAddToPayment = async (ids: number[], dueDate: Date) => {
    const handled = handleResult(await addToPayment(ids, dueDate), () => null)
    showFlashMessage(handled, i18n.t('expenses_v2.list.actions.modal.add_to_payment.notification'))
    return handled
  }

  const onRemoveFromPayroll = async (ids: number[]) => {
    const handled = handleResult(await removeFromPayroll(ids), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onSetToPaid = async (ids: number[]) => {
    const handled = handleResult(await setToPaid(ids), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onRevertPaid = async (ids: number[]) => {
    const handled = handleResult(await revertPaid(ids), () => null)
    showFlashMessage(
      handled,
      i18n.tp('expenses_v2.success_messages.revert_paid', { count: ids.length })
    )
    return handled
  }

  const onAddToGroup = async (ids: number[], groupId: number) => {
    const handled = handleResult(await addToGroup(ids, groupId), () => null)
    showFlashMessage(handled)
    return handled
  }

  const onRemoveFromGroup = async (ids: number[]) => {
    const handled = handleResult(await removeFromGroup(ids), () => null)
    showFlashMessage(handled)
    return handled
  }

  return {
    loading,
    onApprove,
    onReject,
    onAddToPayroll,
    onAddToPayment,
    onRemoveFromPayroll,
    onRestartReview,
    onSetToPaid,
    onRevertPaid,
    onAddToGroup,
    onRemoveFromGroup,
  }
}
